import React, { useRef, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import shortid from 'shortid'
import moment from 'moment'

import styles from '../component-styles/campaign-codes-table.module.scss'

import {
  CampaignCodeGeneratorStructure,
  CampaignCodesCompressed,
} from '../api-client/api-types'
import Input from './input'
import { getItemByKeyValue, copyString } from '../helpers'
import { formatMetricValue, getMetricStatus } from '../helpers/campaign-codes'
import { dateFormatShort, codeStatus, messages } from '../core/variables'
import ToolTip from './tooltip'
import Button from './button'
import { Preloader } from './loader'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import ConfirmationButtons from './confirmation-buttons'
import Heading from './heading'
import { logActionAsync } from '../store/actions'

interface EditModalProps {
  editKey: string
  onToggle: () => void
  onSave: (form: CampaignCodesCompressed) => void
  form: CampaignCodesCompressed
  isAdobe?: boolean
  show: boolean
}

export function EditModal({
  show,
  editKey,
  onToggle,
  onSave,
  form,
  isAdobe = false,
}: EditModalProps): React.ReactElement {
  const [currentValue, setCurrentValue] = useState<any>(null)
  const [nextForm, setNextForm] = useState<any>(null)
  const type = editKey === 'c' ? 'link' : 'parameter'
  const { cID } = form
  const paramDef =
    type === 'parameter' ? getItemByKeyValue(form.cDef, 'fID', editKey) : null
  const useValue = paramDef ? paramDef.n : form.c
  const dispatch = useDispatch()

  useEffect(() => {
    setCurrentValue(useValue)
    if (show) {
      const actionData = {
        action: `open-edit-modal-${type}`,
        longDescription: '',
        websiteSection: 'track',
      }
      dispatch(logActionAsync(actionData))
    }
  }, [show])

  const updateValue = (value) => {
    let newForm = {
      ...form,
    }
    if (type === 'link') {
      newForm = {
        ...form,
        c: value,
      }
    } else {
      // eslint-disable-next-line
      // debugger
      const nextCodeDef = !paramDef
        ? [...form.cDef, { fID: editKey, n: value, v: '' }]
        : [
            ...form.cDef.map((item: any): any => {
              const res =
                item.fID === paramDef.fID
                  ? {
                      ...item,
                      n: value,
                    }
                  : item
              return res
            }),
          ]
      newForm = {
        ...form,
        cDef: nextCodeDef,
      }
    }
    setNextForm(newForm)
    setCurrentValue(value)
  }

  const saveValue = () => {
    if (nextForm !== null) {
      onSave(nextForm)
    }
    const actionData = {
      action: `update-code-${type}`,
      longDescription: '',
      websiteSection: 'track',
    }
    dispatch(logActionAsync(actionData))
  }

  const hasChanged = !(useValue === currentValue)
  return (
    <Modal
      active={show}
      toggleActive={() => {
        onToggle()
      }}
      type="white"
      narrow
      isWarning
    >
      <ModalHead>
        <Heading type={3} align="left">
          {type === 'link'
            ? `Edit this landing page with campaign code?`
            : `Edit this parameter?`}
        </Heading>
      </ModalHead>
      <ModalBody className={styles.modalBody}>
        {type === 'link' && (
          <>
            <p>This action will change:</p>
            <ul>
              <li>The landing page for live short links</li>
              <li>The metric data matched and returned in Uplifter</li>
            </ul>
            <p>If this link has been used, we recommend creating a new link.</p>
          </>
        )}
        {type === 'parameter' && !isAdobe && (
          <>
            <p>
              This action will recategorise all data associated with this link{' '}
              <strong>in Uplifter, but not Google Analytics</strong>.
            </p>
            <p>
              It will not automatically change the landing page with campaign
              code, however you can edit this separately.
            </p>
          </>
        )}
        {type === 'parameter' && isAdobe && (
          <>
            <p>
              This action will recategorise all data associated with this link{' '}
              <strong>in Uplifter and Adobe Analytics</strong>.
            </p>
            <p>
              It will not automatically change the landing page with campaign
              code, however you can edit this separately.
            </p>
          </>
        )}
        <p>If you understand the impact, edit below and save:</p>
        {show && (
          <Input
            id={`code-${cID}`}
            name="name"
            value={useValue}
            onKeyUp={(event) => {
              if (event.keyCode === 13) {
                onToggle()
                saveValue()
              }
            }}
            onValueChange={(value: string): void => {
              updateValue(value)
            }}
            type={type === 'link' ? 'textArea' : 'input'}
            multilineInput={type === 'link'}
            maxLength={2048}
            // textAreaHeight={height}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          disabled={!hasChanged}
          type="simple"
          onYes={() => {
            onToggle()
            saveValue()
          }}
          onNo={(): void => {
            const actionData = {
              action: `cancel-edit-modal-${type}`,
              longDescription: '',
              websiteSection: 'track',
            }
            dispatch(logActionAsync(actionData))
            onToggle()
          }}
          yesLabel="Save"
          noLabel="Cancel"
        />
      </ModalFooter>
    </Modal>
  )
}

export function getUrlMessages(
  validatedUrl: any,
  createdToday: boolean,
  hasMetricData: boolean,
  isGoodUrl: boolean,
) {
  const urlMessages: string[] = []

  if (validatedUrl) {
    if (isGoodUrl) {
      urlMessages.push(messages.validateUrlMessage.goodUrl)
    } else if (validatedUrl.badUrl) {
      urlMessages.push(messages.validateUrlMessage.badUrl)
    } else if (validatedUrl.slowLandingPage) {
      urlMessages.push(messages.validateUrlMessage.slowLandingPage)
    } else if (validatedUrl.noAnalyticsTag) {
      urlMessages.push(messages.validateUrlMessage.noAnalyticsTag)
    } else if (validatedUrl.redirectedLandingPage) {
      urlMessages.push(messages.validateUrlMessage.redirectedLandingPage)
    }
  } else if (createdToday) {
    urlMessages.push(messages.validateUrlMessage.validating)
  } else if (!hasMetricData) {
    urlMessages.push(messages.validateUrlMessage.noData)
  } else {
    urlMessages.push(messages.validateUrlMessage.unknownUrl)
  }
  return urlMessages.join('')
}

interface MetricItemsProps {
  rowIndex: number
  selectedMetrics: any
  selectedMetricsIndexes: number[]
  loading: boolean
  storedCodeStats: any
  codeIdIndex: number
}

export function MetricItems({
  rowIndex,
  selectedMetrics,
  selectedMetricsIndexes,
  loading,
  storedCodeStats,
  codeIdIndex,
}: MetricItemsProps): React.ReactElement {
  const { metricValues } = storedCodeStats || { metricValues: [] }
  return (
    <>
      {selectedMetricsIndexes.map((selectedMetricsIndex) => {
        if (codeIdIndex !== -1) {
          const metric = metricValues[selectedMetricsIndex]
          const { mf365, units } = metric
          const value = mf365[codeIdIndex]
          const formattedNumber = formatMetricValue(value, units)

          return (
            <td key={shortid.generate()}>
              <div className={styles.matchWrapper}>
                <span>{formattedNumber}</span>
              </div>
            </td>
          )
        }

        return (
          <td
            key={shortid.generate()}
            className={classNames({
              [styles.loadingCell]: loading,
            })}
          />
        )
      })}
    </>
  )
}

// interface CodeElementProps {
//   form: CampaignCodesCompressed
//   setForm: (f: CampaignCodesCompressed) => void
//   updateParam: (f: CampaignCodesCompressed) => void
//   height?: number
// }

// export function CodeElement({
//   form,
//   setForm,
//   updateParam,
//   height,
// }: CodeElementProps): React.ReactElement {
//   const { cID } = form
//   return (
//     <ClickEditInput
//       id={`code-${cID}`}
//       name="name"
//       value={form.c}
//       formatValue={(value) => {
//         return <>{value}</>
//       }}
//       onChange={(value: string): void => {
//         const nextForm = {
//           ...form,
//           c: value,
//         }

//         setForm(nextForm)
//         updateParam(nextForm)
//       }}
//       type="textArea"
//       multilineInput
//       maxLength={2048}
//       textAreaHeight={height}
//     />
//   )
// }

interface UrlStatusProps {
  validatedUrl: any
  createdToday: boolean
  hasMetricData: boolean
  isFirstRow: boolean
}
export function UrlStatus({
  validatedUrl,
  createdToday,
  hasMetricData,
  isFirstRow,
}: UrlStatusProps): React.ReactElement | null {
  const noData = !validatedUrl
  const isBadUrl = validatedUrl && validatedUrl.badUrl
  const isGoodUrl =
    !isBadUrl &&
    validatedUrl &&
    !validatedUrl.slowLandingPage &&
    !validatedUrl.noAnalyticsTag &&
    !validatedUrl.redirectedLandingPage

  const urlStatusMessage = getUrlMessages(
    validatedUrl,
    createdToday,
    hasMetricData,
    isGoodUrl,
  )

  if (noData) {
    if (createdToday) {
      return (
        <div className={classNames(styles.badge)}>
          <ToolTip
            showOnLeft
            message={urlStatusMessage}
            className={styles.tooltip}
          >
            <span className={styles.blank} />
          </ToolTip>
        </div>
      )
    }
    if (!hasMetricData) {
      return (
        <div className={classNames(styles.badge)}>
          <ToolTip
            showOnLeft
            message={urlStatusMessage}
            className={styles.tooltip}
          >
            <span className={styles.blank} />
          </ToolTip>
        </div>
      )
    }

    return (
      <div className={classNames(styles.badge)}>
        <ToolTip
          showOnLeft
          message={urlStatusMessage}
          className={styles.tooltip}
        >
          <span className={styles.blank} />
        </ToolTip>
      </div>
    )
  }

  if (isGoodUrl) {
    return (
      <div className={classNames(styles.badge, styles.goodUrl)}>
        <ToolTip
          showOnLeft
          message={urlStatusMessage}
          className={styles.tooltip}
        >
          <span className={styles.blank} />
        </ToolTip>
      </div>
    )
  }

  if (validatedUrl && !isGoodUrl) {
    return (
      <div
        className={classNames(styles.badge, {
          [styles.badUrl]: isBadUrl,
          [styles.slowLandingPage]: !isBadUrl && validatedUrl.slowLandingPage,
          [styles.noAnalyticsTag]: !isBadUrl && validatedUrl.noAnalyticsTag,
          [styles.redirectedLandingPage]:
            !isBadUrl && validatedUrl.redirectedLandingPage,
        })}
      >
        <ToolTip
          message={urlStatusMessage}
          className={styles.tooltip}
          showOnLeft
        >
          <span className={styles.blank} />
        </ToolTip>
      </div>
    )
  }

  return null
}

export function hasData(statsItem) {
  if (!statsItem) {
    return false
  }
  let res = 0
  Object.keys(statsItem).forEach((key) => {
    if (typeof statsItem[key] === 'number') {
      res += statsItem[key]
    }
  })
  return res > 0
}

export interface CodeProps {
  rowIndex: number
  isAdobe: boolean
  cellsOrder: string[]
  code: CampaignCodesCompressed
  updateParam: (form: CampaignCodesCompressed) => void
  isWideScreen: boolean
  selectedCodes: string[]
  setSelectedCodes: (cID: string, checked: boolean) => void
  selectedMetrics: string[]
  loading: boolean
  generatedStructure: CampaignCodeGeneratorStructure
  validatedUrl: any
  lastRow: boolean
  selectedMetricsIndexes: number[]
  canEdit: boolean
  storedCodeStats: any
}

export default function CampaignCode({
  rowIndex,
  isAdobe,
  cellsOrder,
  code,
  updateParam,
  selectedMetrics,
  isWideScreen,
  selectedCodes,
  setSelectedCodes,
  loading,
  generatedStructure,
  validatedUrl,
  lastRow,
  selectedMetricsIndexes,
  canEdit,
  storedCodeStats,
}: CodeProps): React.ReactElement | null {
  const [showModal, setShowModal] = useState(false)
  const [modalKey, setModalKey] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  // const [codeHeight, setCodeHeight] = useState<number | null>(null)
  // useEffect(() => {
  //   if (ref && ref.current) {
  //     setCodeHeight(ref.current.offsetHeight + 4)
  //   }
  // }, [])

  const [form, setForm] = useState(code)
  const dateObject = moment(form.t)
  const { cID } = form
  const {
    codeIDs,
    status: storedCodeStatStatus,
    metricValues,
  } = storedCodeStats
  const codeIdIndex = codeIDs.indexOf(cID)
  let hasMetricData = false
  if (codeIdIndex !== -1) {
    const vals = metricValues
      .map((i) => i.mf365[codeIdIndex])
      .filter((i) => i > 0)
    hasMetricData = vals.length > 0
  }

  const createdToday = moment().diff(dateObject, 'days') === 0
  const codeType = getMetricStatus(form.t, storedCodeStatStatus, codeIdIndex)

  // <CodeElement
  //    key={`code-${cID}`}
  //    form={form}
  //    setForm={setForm}
  //    updateParam={updateParam}
  //    height={codeHeight || undefined}
  //  />

  return (
    <>
      <tr id={`cID-${cID}`}>
        <td className={styles.checkboxCell}>
          <Input
            type="checkbox"
            id={`checkbox-${cID}`}
            name={`checkbox-${cID}`}
            checked={!!(cID && selectedCodes.indexOf(cID) !== -1)}
            className={styles.checkboxItem}
            label=" "
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setSelectedCodes(cID || '', checked)
            }}
          />
        </td>
        <td>
          <span className={styles.createdWrapper}>
            <span className={styles.created}>
              <span className={styles.date}>
                {dateObject.format(dateFormatShort)}
              </span>
              <span className={styles.timeSince}>
                {dateObject.format('HH:mm')}
              </span>
            </span>
            <span className={styles.user}>{form.e || 'Account user'}</span>
          </span>
        </td>
        <td className={classNames(styles.shortLinkCell)}>
          <div className={styles.linkCodeWrapper}>
            <span className={styles.linkCode}>
              {form.sL !== '' ? `https://upl.inc/${form.sL}` : ''}
            </span>
            {form.sL !== '' && (
              <Button
                type="copy"
                align="left"
                className={styles.copyButton}
                onClick={(): void => {
                  const copyLink =
                    form.sL === '' ? form.c : `https://upl.inc/${form.sL}`
                  copyString(copyLink)
                }}
              />
            )}
          </div>
        </td>
        <td
          className={classNames(styles.codeCell, {
            [styles.wideScreen]: isWideScreen,
          })}
        >
          <span ref={ref} className={styles.codeCellInner}>
            <div
              className={classNames(styles.codeTextWrapper, {
                [styles.codeTextWrapperHasButtons]: canEdit,
              })}
            >
              <span className={styles.codeText}>{form.c}</span>
              {canEdit && (
                <Button
                  className={styles.deleteButton}
                  type="editText"
                  onClick={() => {
                    setShowModal(true)
                    setModalKey('c')
                  }}
                />
              )}
            </div>
            <UrlStatus
              isFirstRow={rowIndex === 0}
              validatedUrl={validatedUrl}
              createdToday={createdToday}
              hasMetricData={hasMetricData}
            />
          </span>
        </td>
        <td className={styles.statusCell}>
          {loading ? (
            <Preloader style={{ width: 25, height: 16 }} />
          ) : (
            <div
              className={classNames(styles.button, {
                [styles.unknownButton]: codeType === codeStatus.na,
                [styles.newButton]: codeType === codeStatus.new,
                [styles.unusedButton]: codeType === codeStatus.unused,
                [styles.liveButton]: codeType === codeStatus.live,
                [styles.endedButton]: codeType === codeStatus.ended,
              })}
            >
              {codeType && (
                <ToolTip
                  showUnder={!lastRow}
                  message={messages.linkStatus[codeType]}
                  className={styles.tooltip}
                >
                  {codeType}
                </ToolTip>
              )}
              {!codeType && <>{codeType}</>}
            </div>
          )}
        </td>
        <MetricItems
          storedCodeStats={storedCodeStats}
          selectedMetricsIndexes={selectedMetricsIndexes}
          rowIndex={rowIndex}
          key={`metric-${cID}`}
          selectedMetrics={selectedMetrics}
          loading={loading}
          codeIdIndex={codeIdIndex}
        />
        {cellsOrder.map(
          (fieldID): React.ReactElement => {
            const paramDef = getItemByKeyValue(form.cDef, 'fID', fieldID)

            const useValue = paramDef ? paramDef.n : ''
            return (
              <td key={`td-${fieldID}`} className={styles.containsInput}>
                <div
                  className={classNames(styles.codeTextWrapper, {
                    [styles.codeTextWrapperHasButtons]: canEdit,
                  })}
                >
                  <span className={styles.codeText}>{useValue}</span>
                  {canEdit && (
                    <Button
                      className={styles.deleteButton}
                      type="editText"
                      onClick={() => {
                        setShowModal(true)
                        setModalKey(fieldID)
                      }}
                    />
                  )}
                </div>
              </td>
            )
          },
        )}
      </tr>
      <EditModal
        show={showModal}
        isAdobe={isAdobe}
        editKey={modalKey}
        form={form}
        onToggle={() => {
          setShowModal(false)
        }}
        onSave={(nextForm) => {
          setForm(nextForm)
          updateParam(nextForm)
        }}
      />
    </>
  )
}
